const SUCCESSFUL_PAYMENT_STATUSES = new Set([
  "SUCCESS",
  "COMPLETED",
  "PAID",
  "CAPTURED",
]);

export const isSuccessfulPayment = (payment) => {
  if (!payment || !payment.status) {
    return false;
  }
  return SUCCESSFUL_PAYMENT_STATUSES.has(String(payment.status).toUpperCase());
};

export const getSuccessfulPayments = (payments) =>
  Array.isArray(payments) ? payments.filter(isSuccessfulPayment) : [];

export const getPaymentAmount = (payment) => {
  const amount =
    payment?.amount ?? payment?.amountPaid ?? payment?.amount_paid ?? 0;
  const parsedAmount = parseFloat(amount);
  return Number.isFinite(parsedAmount) ? parsedAmount : 0;
};

export const hasSuccessfulPaymentRecord = (payments) =>
  getSuccessfulPayments(payments).length > 0;

export const getPaperDiscountPercentage = (paperSequence, paperDiscountRules) => {
  if (
    !paperSequence ||
    !Array.isArray(paperDiscountRules) ||
    paperDiscountRules.length === 0
  ) {
    return 0;
  }

  const matchedRule = paperDiscountRules.find((rule) => {
    const from = Number.isFinite(parseInt(rule.paperSequenceFrom, 10))
      ? parseInt(rule.paperSequenceFrom, 10)
      : 1;
    const to = Number.isFinite(parseInt(rule.paperSequenceTo, 10))
      ? parseInt(rule.paperSequenceTo, 10)
      : Number.MAX_SAFE_INTEGER;
    return paperSequence >= from && paperSequence <= to;
  });

  if (!matchedRule) {
    return 0;
  }

  const pct = parseFloat(matchedRule.value);
  if (!Number.isFinite(pct) || pct <= 0) {
    return 0;
  }
  return Math.min(pct, 100);
};

const getAccompanyingPersonKey = (person) => {
  if (person?.id != null) {
    return `id:${person.id}`;
  }
  return `name:${(person?.name || "").trim().toLowerCase()}`;
};

const matchesAccompanyingPerson = (record, person, accompanyingPersonFee) => {
  if (
    !record ||
    !accompanyingPersonFee ||
    record.feeTypeId !== accompanyingPersonFee.id
  ) {
    return false;
  }

  const personKeyById = person?.id != null ? `id:${person.id}` : null;
  const personKeyByName = `name:${(person?.name || "").trim().toLowerCase()}`;
  const recordKeyById = record.id != null ? `id:${record.id}` : null;
  const recordKeyByName = `name:${(record.name || "").trim().toLowerCase()}`;

  return (
    (personKeyById && recordKeyById === personKeyById) ||
    (personKeyByName && recordKeyByName === personKeyByName)
  );
};

const resolveAdditionalFeeRate = ({
  fee,
  accompanyingPersonFee,
  focusedSessionFees,
}) => {
  let itemRate = 0;
  let rateSource = "unknown";

  if (fee.rate) {
    itemRate = parseFloat(fee.rate);
    rateSource = "fee.rate";
  }

  if (
    itemRate === 0 &&
    accompanyingPersonFee &&
    fee.feeTypeId === accompanyingPersonFee.id
  ) {
    itemRate = parseFloat(accompanyingPersonFee.rate || 0);
    rateSource = "accompanyingPersonFee";
  }

  if (itemRate === 0 && focusedSessionFees[fee.feeTypeId]) {
    itemRate = parseFloat(focusedSessionFees[fee.feeTypeId].rate || 0);
    rateSource = "focusedSessionFees";
  }

  if (!Number.isFinite(itemRate) || itemRate === 0) {
    console.warn(
      `⚠️ Rate not configured for fee: ${fee.name || fee.feeTypeId} (feeTypeId: ${fee.feeTypeId})`,
      `attempted sources: fee.rate=${fee.rate}, focusedSessionFees[${fee.feeTypeId}]=${focusedSessionFees[fee.feeTypeId]?.rate}`
    );
    itemRate = 0;
  }

  return { fee, itemRate, rateSource };
};

export const calculateOutstandingAmounts = ({
  registrationFee,
  formData,
  selectedFocusedSessions,
  accompanyingPersons,
  accompanyingPersonFee,
  focusedSessionFees,
  conveyanceFeePercentage,
  hasPostPaymentCategoryOrTypeChange,
}) => {
  const regFee = registrationFee ? parseFloat(registrationFee.rate || 0) : 0;
  const existingAdditionalFees = formData.additionalFees || [];
  const existingZeroPayments = formData.zeroPayments || [];

  console.log("🔍 OUTSTANDING CALCULATION START");
  console.log("  selectedFocusedSessions:", selectedFocusedSessions);
  console.log("  existingAdditionalFees count:", existingAdditionalFees.length);
  console.log("  existingZeroPayments count:", existingZeroPayments.length);
  existingAdditionalFees.forEach((fee) => {
    console.log(
      `    - ${fee.name} (feeTypeId: ${fee.feeTypeId}, rate: ${fee.rate}, paymentId: ${fee.paymentId}, paid: ${fee.paid})`
    );
  });
  existingZeroPayments.forEach((fee) => {
    console.log(
      `    - [zeroPayment] ${fee.name} (feeTypeId: ${fee.feeTypeId}, rate: ${fee.rate}, paymentId: ${fee.paymentId}, paid: ${fee.paid})`
    );
  });

  const payments = formData.payment || [];
  const registrationHasSuccessfulPayment = hasSuccessfulPaymentRecord(payments);
  const isRegistrationPaid = registrationHasSuccessfulPayment || formData.paymentId;
  let registrationOutstanding = regFee;

  if (isRegistrationPaid && !hasPostPaymentCategoryOrTypeChange) {
    registrationOutstanding = 0;
  }

  console.log("💳 Registration Payment Status:");
  console.log("  - isRegistrationPaid:", isRegistrationPaid);
  console.log("  - formData.paymentId:", formData.paymentId);
  console.log("  - registrationHasSuccessfulPayment:", registrationHasSuccessfulPayment);
  console.log("  - hasPostPaymentCategoryOrTypeChange:", hasPostPaymentCategoryOrTypeChange);
  console.log("  - registrationOutstanding:", registrationOutstanding);

  const currentAccompanyingKeys = new Set(
    accompanyingPersons.map(getAccompanyingPersonKey)
  );
  const currentFocusedSessionSet = new Set(selectedFocusedSessions);

  const outstandingItems = existingAdditionalFees.filter((fee) => {
    if (fee.paymentId) {
      console.log(
        `  ⏭️ Skipping fee (has paymentId):`,
        fee.name,
        "paymentId:",
        fee.paymentId
      );
      return false;
    }

    if (accompanyingPersonFee && fee.feeTypeId === accompanyingPersonFee.id) {
      const keyById = fee.id != null ? `id:${fee.id}` : null;
      const keyByName = `name:${(fee.name || "").trim().toLowerCase()}`;
      const isIncluded =
        (keyById && currentAccompanyingKeys.has(keyById)) ||
        currentAccompanyingKeys.has(keyByName);
      if (isIncluded) {
        console.log(
          `  ✅ INCLUDING accompanying person fee:`,
          fee.name,
          "id:",
          fee.id,
          "rate:",
          fee.rate
        );
      }
      return isIncluded;
    }

    const isFocusedSession =
      fee.feeTypeId && fee.feeTypeId !== accompanyingPersonFee?.id;
    if (isFocusedSession) {
      console.log(
        `  ✅ INCLUDING unpaid focused session fee:`,
        fee.name,
        "id:",
        fee.id,
        "feeTypeId:",
        fee.feeTypeId,
        "rate:",
        fee.rate,
        "paymentId:",
        fee.paymentId
      );
    }
    return isFocusedSession;
  });

  const outstandingItemRates = outstandingItems.map((fee) =>
    resolveAdditionalFeeRate({
      fee,
      accompanyingPersonFee,
      focusedSessionFees,
    })
  );

  if (outstandingItemRates.length > 0) {
    console.log("🔎 outstanding item rates:");
    outstandingItemRates.forEach(({ fee, itemRate, rateSource }) => {
      console.log(
        `  - ${fee.name || fee.feeTypeId} (feeTypeId: ${fee.feeTypeId}, id: ${fee.id}) -> rate: ${itemRate} (source: ${rateSource})`
      );
    });
  }

  const outstandingFromAdditionalFees = outstandingItemRates.reduce(
    (sum, r) => sum + (r.itemRate || 0),
    0
  );

  const newAccompanyingCount = Math.max(
    0,
    accompanyingPersons
      .filter((p) => p.name && p.name.trim() !== "")
      .filter((p) => {
        const existsInAdditionalFees = existingAdditionalFees.some((fee) =>
          matchesAccompanyingPerson(fee, p, accompanyingPersonFee)
        );
        const existsInZeroPayments = existingZeroPayments.some((fee) =>
          matchesAccompanyingPerson(fee, p, accompanyingPersonFee)
        );
        return !existsInAdditionalFees && !existsInZeroPayments;
      }).length
  );
  const accompanyingRate = accompanyingPersonFee
    ? parseFloat(accompanyingPersonFee.rate || 0)
    : 0;
  const newAccompanyingOutstanding = newAccompanyingCount * accompanyingRate;

  const newFocusedSessionIds = selectedFocusedSessions.filter((sessionId) => {
    const existsInAdditionalFees = existingAdditionalFees.some(
      (fee) => fee.feeTypeId === sessionId
    );
    const existsInZeroPayments = existingZeroPayments.some(
      (fee) => fee.feeTypeId === sessionId
    );

    return !existsInAdditionalFees && !existsInZeroPayments;
  });
  const newFocusedOutstanding = newFocusedSessionIds.reduce((sum, sessionId) => {
    let sessionRate = 0;
    const sessionFee = focusedSessionFees[sessionId];
    if (sessionFee && sessionFee.rate) {
      sessionRate = parseFloat(sessionFee.rate);
    }
    return sum + sessionRate;
  }, 0);

  const dbAdditionalTotal = (existingAdditionalFees || []).reduce((sum, fee) => {
    if (fee.paymentId) {
      return sum;
    }

    let itemRate = fee.rate ? parseFloat(fee.rate) : 0;
    if (
      itemRate === 0 &&
      accompanyingPersonFee &&
      fee.feeTypeId === accompanyingPersonFee.id
    ) {
      itemRate = parseFloat(accompanyingPersonFee.rate || 0);
    } else if (itemRate === 0 && focusedSessionFees[fee.feeTypeId]) {
      itemRate = parseFloat(focusedSessionFees[fee.feeTypeId].rate || 0);
    }
    return sum + itemRate;
  }, 0);

  const totalDueBeforeFee =
    regFee + dbAdditionalTotal + newAccompanyingOutstanding + newFocusedOutstanding;
  const successfulPaymentsTotal = payments.reduce((sum, p) => {
    if (isSuccessfulPayment(p)) {
      return sum + getPaymentAmount(p);
    }
    return sum;
  }, 0);

  console.log("💰 PAYMENT ANALYSIS:");
  console.log("  Total Due (before conveyance fee):", totalDueBeforeFee);
  console.log("  Total Paid (successful payments):", successfulPaymentsTotal);
  console.log("  Difference (due - paid):", totalDueBeforeFee - successfulPaymentsTotal);

  let totalOutstandingBeforeFee =
    registrationOutstanding +
    outstandingFromAdditionalFees +
    newAccompanyingOutstanding +
    newFocusedOutstanding;

  if (!Number.isFinite(totalOutstandingBeforeFee)) {
    console.warn(
      "⚠️ Computed totalOutstandingBeforeFee is non-finite:",
      totalOutstandingBeforeFee
    );
    totalOutstandingBeforeFee = Math.max(0, Number(totalOutstandingBeforeFee) || 0);
  }

  console.log("📊 Outstanding Calculation Breakdown:");
  console.log("  registrationOutstanding:", registrationOutstanding);
  console.log("  outstandingFromAdditionalFees:", outstandingFromAdditionalFees);
  console.log("  outstandingItems details (with resolved rates):");
  outstandingItemRates.forEach(({ fee, itemRate, rateSource }) => {
    console.log(
      `    - ${fee.name} (feeTypeId: ${fee.feeTypeId}, dbRate: ${fee.rate}, resolvedRate: ${itemRate}, source: ${rateSource}, paymentId: ${fee.paymentId})`
    );
  });
  console.log("  newAccompanyingOutstanding:", newAccompanyingOutstanding);
  console.log("  newAccompanyingCount:", newAccompanyingCount);
  console.log("  newFocusedOutstanding:", newFocusedOutstanding);
  console.log("  newFocusedSessionIds:", newFocusedSessionIds);
  console.log("  selectedFocusedSessions:", selectedFocusedSessions);
  console.log("  existingAdditionalFees (all):");
  existingAdditionalFees.forEach((fee) => {
    console.log(
      `    - ${fee.name} (feeTypeId: ${fee.feeTypeId}, paymentId: ${fee.paymentId}, rate: ${fee.rate})`
    );
  });
  console.log("  ---");
  console.log(
    "  RESULT: totalOutstandingBeforeFee (component sum):",
    totalOutstandingBeforeFee
  );

  const conveyanceFeeRate =
    conveyanceFeePercentage !== null && conveyanceFeePercentage > 0
      ? conveyanceFeePercentage / 100
      : 0;
  const conveyanceFeeAmount = totalOutstandingBeforeFee * conveyanceFeeRate;
  const totalOutstanding = totalOutstandingBeforeFee + conveyanceFeeAmount;

  return {
    regFee,
    existingAdditionalFees,
    registrationOutstanding,
    outstandingItems,
    outstandingFromAdditionalFees,
    newAccompanyingCount,
    newAccompanyingOutstanding,
    newFocusedSessionIds,
    newFocusedOutstanding,
    totalOutstandingBeforeFee,
    conveyanceFeeRate,
    conveyanceFeeAmount,
    totalOutstanding,
    currency: registrationFee?.currency || (formData.country === "India" ? "INR" : "USD"),
  };
};

export const calculatePaymentAmountSummary = ({
  isAdditionalPayment,
  outstandingSummary,
  formData,
  registrationFee,
  accompanyingPersons,
  accompanyingPersonFee,
  selectedFocusedSessions,
  focusedSessionFees,
  conveyanceFeePercentage,
  shouldApplyPaperDiscount,
  paperDiscountPercentage,
  currentPaperSequence,
}) => {
  let currency;
  let amountInSmallestUnit;
  let actualAmount;

  console.log("💵 Starting fee calculation:");
  console.log("  formData.paid:", formData.paid);
  console.log("  registrationFee:", registrationFee);
  console.log("  accompanyingPersons.length:", accompanyingPersons.length);
  console.log("  selectedFocusedSessions.length:", selectedFocusedSessions.length);
  console.log("  isAdditionalPayment:", isAdditionalPayment);

  if (isAdditionalPayment) {
    const { totalOutstanding, conveyanceFeeAmount } = outstandingSummary;
    currency =
      outstandingSummary.currency || (formData.country === "India" ? "INR" : "USD");
    actualAmount = totalOutstanding;
    amountInSmallestUnit = Math.round(totalOutstanding * 100);

    console.log(
      "💰 Fee Calculation (ADDITIONAL PAYMENT via calculateOutstandingAmounts):"
    );
    console.log("  totalOutstanding:", totalOutstanding);
    console.log("  conveyanceFeeAmount:", conveyanceFeeAmount);
    console.log("  amountInSmallestUnit:", amountInSmallestUnit);
    console.log("  currency:", currency);
  } else if (registrationFee && registrationFee.rate) {
    currency = registrationFee.currency || "INR";
    console.log("  isAdditionalPayment: false (handled above)");

    const baseRate = parseFloat(registrationFee.rate || 0);
    const baseDiscountAmount = shouldApplyPaperDiscount
      ? (baseRate * paperDiscountPercentage) / 100
      : 0;
    const discountedBaseRate = Math.max(0, baseRate - baseDiscountAmount);
    actualAmount = discountedBaseRate;

    if (
      accompanyingPersons.length > 0 &&
      accompanyingPersonFee &&
      accompanyingPersonFee.rate
    ) {
      const accompanyingTotal =
        parseFloat(accompanyingPersonFee.rate) * accompanyingPersons.length;
      actualAmount += accompanyingTotal;
    }

    if (selectedFocusedSessions.length > 0) {
      selectedFocusedSessions.forEach((sessionId) => {
        const sessionFee = focusedSessionFees[sessionId];
        if (
          sessionFee &&
          sessionFee.rate &&
          parseFloat(sessionFee.rate) > 0
        ) {
          actualAmount += parseFloat(sessionFee.rate);
        }
      });
    }

    const conveyanceFeeRate =
      conveyanceFeePercentage !== null && conveyanceFeePercentage > 0
        ? conveyanceFeePercentage / 100
        : 0;
    const conveyanceFee = actualAmount * conveyanceFeeRate;
    const totalAmountWithFee = actualAmount + conveyanceFee;
    amountInSmallestUnit = Math.round(totalAmountWithFee * 100);

    console.log("💰 Fee Calculation Results:");
    console.log("  registrationFee:", registrationFee);
    console.log("  paperDiscountPercentage:", paperDiscountPercentage);
    console.log("  currentPaperSequence:", currentPaperSequence);
    console.log("  actualAmount (before conveyance fee):", actualAmount);
    console.log("  conveyanceFeePercentage:", conveyanceFeePercentage, "%");
    console.log("  conveyanceFeeRate:", conveyanceFeeRate);
    console.log("  conveyanceFee:", conveyanceFee);
    console.log("  totalAmountWithFee:", totalAmountWithFee);
    console.log("  amountInSmallestUnit:", amountInSmallestUnit);
    console.log("  currency:", currency);
  } else {
    currency = formData.country === "India" ? "INR" : "USD";
    amountInSmallestUnit = formData.country === "India" ? 10000 : 100;
    actualAmount = amountInSmallestUnit / 100;

    console.log("💰 Fee Calculation (FALLBACK):");
    console.log("  No registrationFee found, using fallback");
    console.log("  actualAmount:", actualAmount);
    console.log("  amountInSmallestUnit:", amountInSmallestUnit);
    console.log("  currency:", currency);
  }

  return {
    currency,
    amountInSmallestUnit,
    actualAmount,
  };
};

export const buildPaymentBreakdown = ({
  isAdditionalPayment,
  formData,
  registrationFee,
  hasPostPaymentCategoryOrTypeChange,
  shouldApplyPaperDiscount,
  paperDiscountPercentage,
  currentPaperSequence,
  currency,
  accompanyingPersons,
  accompanyingPersonFee,
  selectedFocusedSessions,
  focusedSessionFees,
  focusedSessions,
  conveyanceFeePercentage,
}) => {
  const breakdown = [];

  console.log("🔨 Building payment breakdown array...");
  console.log("  formData.paid:", formData.paid);
  console.log("  accompanyingPersons:", accompanyingPersons);
  console.log("  selectedFocusedSessions:", selectedFocusedSessions);
  console.log("  isAdditionalPayment:", isAdditionalPayment);

  if (isAdditionalPayment) {
    const existingAdditionalFees = formData.additionalFees || [];

    if (registrationFee && registrationFee.rate && hasPostPaymentCategoryOrTypeChange) {
      const changedBaseRate = parseFloat(registrationFee.rate || 0);
      const changedBaseDiscountAmount = shouldApplyPaperDiscount
        ? (changedBaseRate * paperDiscountPercentage) / 100
        : 0;
      const changedDiscountedBaseRate = Math.max(
        0,
        changedBaseRate - changedBaseDiscountAmount
      );
      breakdown.push({
        type: "BASE_FEE",
        feeId: registrationFee.id || null,
        amount: changedDiscountedBaseRate,
        currency: registrationFee.currency || currency,
        description: `Registration Fee - ${formData.category} (${formData.registrationType})`,
      });
      console.log("    ✅ Added BASE_FEE for changed category/type");
    }

    console.log("  📊 Building breakdown for additional payment:");
    console.log("    Total additionalFees:", existingAdditionalFees.length);

    const existingAccompanyingCount = existingAdditionalFees.filter(
      (fee) =>
        accompanyingPersonFee &&
        fee.feeTypeId === accompanyingPersonFee.id &&
        fee.paymentId
    ).length;

    console.log("    Existing PAID accompanying count:", existingAccompanyingCount);
    console.log("    Current accompanying persons:", accompanyingPersons.length);

    const existingFocusedSessionIds = existingAdditionalFees
      .filter((fee) => {
        if (!accompanyingPersonFee) return fee.paymentId;
        return fee.feeTypeId !== accompanyingPersonFee.id && fee.paymentId;
      })
      .map((fee) => fee.feeTypeId);

    console.log("    Existing PAID focused session IDs:", existingFocusedSessionIds);
    console.log("    Currently selected focused sessions:", selectedFocusedSessions);

    if (
      accompanyingPersons.length > existingAccompanyingCount &&
      accompanyingPersonFee &&
      accompanyingPersonFee.rate
    ) {
      const newPersons = accompanyingPersons.slice(existingAccompanyingCount);
      newPersons.forEach((person, index) => {
        breakdown.push({
          type: "ADDITIONAL_FEE",
          feeId: accompanyingPersonFee.id || null,
          amount: parseFloat(accompanyingPersonFee.rate),
          currency: accompanyingPersonFee.currency || currency,
          description: `Accompanying Person - ${
            person.name || `Person ${existingAccompanyingCount + index + 1}`
          }`,
        });
      });
    }

    if (selectedFocusedSessions.length > 0) {
      selectedFocusedSessions.forEach((sessionId) => {
        if (!existingFocusedSessionIds.includes(sessionId)) {
          const sessionFee = focusedSessionFees[sessionId];
          const session = focusedSessions.find((s) => s.id === sessionId);

          if (
            sessionFee &&
            session &&
            sessionFee.rate &&
            parseFloat(sessionFee.rate) > 0
          ) {
            breakdown.push({
              type: "ADDITIONAL_FEE",
              feeId: sessionId,
              amount: parseFloat(sessionFee.rate),
              currency: sessionFee.currency || currency,
              description: session.value || session.name || `Session ${sessionId}`,
            });
          }
        }
      });
    }
  } else {
    console.log("  📝 Building breakdown for INITIAL payment (includes base fee)");

    if (registrationFee && registrationFee.rate) {
      const baseRate = parseFloat(registrationFee.rate || 0);
      const baseDiscountAmount = shouldApplyPaperDiscount
        ? (baseRate * paperDiscountPercentage) / 100
        : 0;
      const discountedBaseRate = Math.max(0, baseRate - baseDiscountAmount);
      console.log("    ✅ Adding BASE_FEE to breakdown");
      console.log("       feeId:", registrationFee.id);
      console.log("       amount:", discountedBaseRate);
      breakdown.push({
        type: "BASE_FEE",
        feeId: registrationFee.id || null,
        amount: discountedBaseRate,
        currency: registrationFee.currency || currency,
        description:
          shouldApplyPaperDiscount && paperDiscountPercentage > 0
            ? `Registration Fee (${paperDiscountPercentage}% paper discount, sequence ${currentPaperSequence})`
            : `Registration Fee - ${formData.category} (${formData.registrationType})`,
      });
    } else {
      console.warn("    ⚠️ Cannot add BASE_FEE - registrationFee or rate is missing");
      console.warn("       registrationFee:", registrationFee);
    }

    console.log("    Checking accompanying persons...");
    console.log("      accompanyingPersons.length:", accompanyingPersons.length);
    console.log("      accompanyingPersonFee:", accompanyingPersonFee);
    if (
      accompanyingPersons.length > 0 &&
      accompanyingPersonFee &&
      accompanyingPersonFee.rate
    ) {
      const unpaidPersons = accompanyingPersons.filter((person) => {
        const alreadyPaid = person.paid || person.paymentId;
        return !alreadyPaid;
      });

      console.log("      unpaidPersons.length:", unpaidPersons.length);
      unpaidPersons.forEach((person, index) => {
        console.log(`      ✅ Adding accompanying person ${index + 1}: ${person.name}`);
        breakdown.push({
          type: "ADDITIONAL_FEE",
          feeId: accompanyingPersonFee.id || null,
          amount: parseFloat(accompanyingPersonFee.rate),
          currency: accompanyingPersonFee.currency || currency,
          description: `Accompanying Person - ${person.name || `Person ${index + 1}`}`,
        });
      });
    } else {
      console.log("      ⏭️  Skipping accompanying persons (none added or fee not loaded)");
    }

    console.log("    Checking focused sessions...");
    console.log("      selectedFocusedSessions.length:", selectedFocusedSessions.length);
    if (selectedFocusedSessions.length > 0) {
      selectedFocusedSessions.forEach((sessionId) => {
        const sessionFee = focusedSessionFees[sessionId];
        const session = focusedSessions.find((s) => s.id === sessionId);

        if (
          sessionFee &&
          session &&
          sessionFee.rate &&
          parseFloat(sessionFee.rate) > 0
        ) {
          console.log(`      ✅ Adding focused session: ${session.value || session.name}`);
          breakdown.push({
            type: "ADDITIONAL_FEE",
            feeId: sessionId,
            amount: parseFloat(sessionFee.rate),
            currency: sessionFee.currency || currency,
            description: session.value || session.name || `Session ${sessionId}`,
          });
        }
      });
    } else {
      console.log("      ⏭️  No focused sessions selected");
    }
  }

  if (
    breakdown.length > 0 &&
    conveyanceFeePercentage !== null &&
    conveyanceFeePercentage > 0
  ) {
    const subtotal = breakdown.reduce(
      (sum, item) => sum + parseFloat(item.amount),
      0
    );
    const conveyanceFeeForBreakdown = subtotal * (conveyanceFeePercentage / 100);

    breakdown.push({
      type: "CONVEYANCE_FEE",
      feeId: null,
      amount: parseFloat(conveyanceFeeForBreakdown.toFixed(2)),
      currency,
      description: `Conveyance Fee (${conveyanceFeePercentage}%)`,
    });

    console.log("💳 Added conveyance fee to breakdown:");
    console.log("  Subtotal:", subtotal);
    console.log("  Conveyance fee percentage:", conveyanceFeePercentage, "%");
    console.log("  Conveyance fee amount:", conveyanceFeeForBreakdown);
    console.log("  Total breakdown items now:", breakdown.length);
  }

  return breakdown;
};
